import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { Client, Task, IndexCard, TaskComment } from '@store/models';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TaskContainerComponent } from 'app/tasks/task/task.container';
import { ConfirmService } from 'app/shared/confirm-dialog/confirm-dialog.service';
import { LuxonUtils } from '@shared';

export interface MyTask {
    lid?: number;
    kid: number;
    dato: Date;
    deadline: Date;
    afsluttet: Date;
    mid: number;
    midName: string;
    tekst: string;
    fup: number;
    fupName: string;
    clientId: number;
    clientName: string;
    kundeansvarlig: number;
}

@Component({
    selector: 'app-samtalelog-ui',
    templateUrl: './samtalelog.component.html',
    styleUrls: ['./samtalelog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SamtalelogComponent {

    noAssignee = false;
    delegated = false;
    @Input() userCard: IndexCard;
    @Input() samtalelogs: Task[];
    @Input() users: IndexCard[];
    @Input() clients: Client[];
    @Output() deleteTask = new EventEmitter<Task>();
    @Output() saveTaskComment = new EventEmitter<Partial<TaskComment>>();
    @Output() clientClick = new EventEmitter<number>();
    @Output() dismiss = new EventEmitter();

    get tasksViewMine(): MyTask[] {
        if (!this.samtalelogs) { return []; }
        const myTasks = this.samtalelogs.filter(task => task.fup === this.userCard.cardId);
        this.sortTasksDeadline(myTasks);
        return this.mapTaskNames(myTasks);
    }

    get tasksViewNoAssignee(): MyTask[] {
        if (!this.samtalelogs) { return []; }
        const noAssigneeTasks = this.samtalelogs.filter(task => task.mid === this.userCard.cardId && (!task.fup || task.fup === 0));
        this.sortTasksDeadline(noAssigneeTasks);
        return this.mapTaskNames(noAssigneeTasks);
    }

    get tasksViewDelegated(): MyTask[] {
        if (!this.samtalelogs) { return []; }
        const noAssigneeTasks = this.samtalelogs.filter(task => task.mid === this.userCard.cardId && task.fup && task.fup !== this.userCard.cardId);
        this.sortTasksDeadline(noAssigneeTasks);
        return this.mapTaskNames(noAssigneeTasks);
    }

    get tasksMine30(): number {
        const tasksWithin30 = this.tasksViewMine.filter(t => (LuxonUtils.fromIso(t.deadline)) < LuxonUtils.now().plus({day: 30}));
        return tasksWithin30.length;
    }

    get tasksNoAssignee30(): number {
        const tasksWithin30 = this.tasksViewNoAssignee.filter(t => (LuxonUtils.fromIso(t.deadline)) < LuxonUtils.now().plus({day: 30}));
        return tasksWithin30.length;
    }

    get tasksDelegated30(): number {
        const tasksWithin30 = this.tasksViewDelegated.filter(t => (LuxonUtils.fromIso(t.deadline)) < LuxonUtils.now().plus({day: 30}));
        return tasksWithin30.length;
    }

    constructor(
        private ngbModal: NgbModal,
        private confirm: ConfirmService
    ) {}

    sortTasksDeadline(tasks: Task[]) {
        tasks.sort((a, b) => {
            if (a.deadline === null) { return -1; }
            if (b.deadline === null) { return 1; }
            return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
        });
    }

    mapTaskNames(tasks: Task[]): MyTask[] {
        if (!this.users || this.users.length < 1) { return []; }
        return tasks.map(task => {
            const midName = task.mid ? this.users.find(u => u.cardId === task.mid)?.navn : '';
            const fupName = task.fup ? this.users.find(u => u.cardId === task.fup)?.navn : '';
            const client = this.clients.find(c => c.kid === task.kid);
            const clientId = client ? client.glkundenr : undefined;
            const kundeansvarlig = client ? client.kundeansvarlig : undefined;
            let clientName = client && client.navn1 ? `${client.navn1},` : '';
            clientName += client && client.navn2 ? client.navn2 : '';
            return <MyTask>{
                ...task,
                midName,
                fupName,
                clientId,
                clientName,
                kundeansvarlig
            };
        });
    }

    statusClass(task: MyTask): string {
        if (task.afsluttet) { return 'task__status--done'; }
        if (!task.fup || task.fup === 0) { return 'task__status--no-assignee'; }
        if (!task.deadline) { return ''; }
        const isBefore = LuxonUtils.now() < LuxonUtils.fromIso(task.deadline);
        return isBefore ? 'task__status--deadline' : 'task__status--overdue';
    }

    canDelete(task: MyTask): boolean {
        const userId = this.userCard.cardId;
        return task ? task.mid === userId || this.userCard.lev >= 4 : false;
    }

    onClientClick(glkundenr: number) {
        this.clientClick.emit(glkundenr);
    }

    onTaskClick(taskId: number) {
        const task = this.samtalelogs.find(s => s.lid === taskId);
        if (!task) { return; }
        const taskModal = this.ngbModal.open(TaskContainerComponent);
        taskModal.componentInstance.task = task;
    }

    onDone(taskId: number) {
        const task = this.samtalelogs.find(s => s.lid === taskId);
        if (!task) { return; }
        const taskModal = this.ngbModal.open(TaskContainerComponent);
        taskModal.componentInstance.task = task;
        taskModal.componentInstance.showDone = true;
    }

    onReject(taskId: number) {
        const task = this.samtalelogs.find(s => s.lid === taskId);
        if (!task) { return; }
        const taskModal = this.ngbModal.open(TaskContainerComponent);
        taskModal.componentInstance.task = task;
        taskModal.componentInstance.showReject = true;
    }

    onDismiss() {
        this.dismiss.emit();
    }

    onDelete(taskId: Number) {
        const task = this.samtalelogs.find(s => s.lid === taskId);
        if (!task) { return; }
        this.confirm.question(`<p>Er du sikker på du vil slette opgaven?</p><p>${task.tekst}</p>`, 'Ja', 'Nej').then(confirmed => {
            if (confirmed) {
                this.onDeleteTask(task);
            }
        });
    }

    onDeleteTask(task: Task) {
        this.saveTaskComment.emit({
            taskId: task.lid,
            created: new Date(),
            creator: this.userCard.cardId,
            type: 'delete'
        });
        const deleteTask = {
            ...task,
            slettet: new Date(),
        };
        this.deleteTask.emit(deleteTask);
    }

}
